import React from 'react'
import { UseFormRegister } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Box, Combobox, ComboboxItem, Input, SingleDate } from '@node-space/ui-components'
import { useDateInput } from 'hooks/forms/useDateInput'
import { AddBeneficiaryFormErrors, AddBeneficiaryFormPayload } from 'types/beneficiaries'

interface IndividualBeneficiaryFieldsProps {
  availableCurrencies: ComboboxItem[]
  alias: string
  countries: ComboboxItem[]
  isCountriesLoading: boolean
  countryCode: string
  currencyCode: string
  errors: AddBeneficiaryFormErrors
  firstName: string
  lastName: string
  dateOfBirth: string
  isLocalBeneficiary?: boolean
  register: UseFormRegister<AddBeneficiaryFormPayload>
  setValue: (name: string, value: string) => void
  renderCurrencySpecificFields: () => React.ReactNode
}

export const IndividualBeneficiaryFields = ({
  availableCurrencies,
  alias,
  countries,
  isCountriesLoading,
  countryCode,
  currencyCode,
  errors,
  firstName,
  lastName,
  dateOfBirth,
  register,
  setValue,
  renderCurrencySpecificFields,
}: IndividualBeneficiaryFieldsProps) => {
  const { t } = useTranslation()

  const { getDateFields, setDateInputValue, setDateErrors, getDateErrors, getDateErrorText } =
    useDateInput()

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(field, event.target.value)
  }

  return (
    <Box flex flexCol>
      <Box paddingB={16}>
        <Combobox
          {...register('currencyCode')}
          label={t('manageBeneficiaries.accountCurrency')}
          id="currency-code"
          selectPlaceholder={t('searchOrSelectACurrency')}
          items={availableCurrencies}
          onChange={value => setValue('currencyCode', value)}
          errorText={errors?.currencyCode?.message}
          defaultValue={currencyCode}
        />
      </Box>
      <Box paddingB={16}>
        <Combobox
          {...register('countryCode')}
          label={t('manageBeneficiaries.countryOfResidence')}
          id="country-code"
          selectPlaceholder={t('searchOrSelectACountry')}
          items={countries}
          onChange={value => setValue('countryCode', value)}
          errorText={errors?.countryCode?.message}
          loading={isCountriesLoading}
          defaultValue={countryCode}
        />
      </Box>

      <Box paddingB={16}>
        <Input
          {...register('firstName')}
          label={t('formFields.firstName')}
          placeholder={t('enterName')}
          inputMode="text"
          name="firstName"
          test-id="first-name"
          errorText={errors?.firstName?.message}
          error={!!errors?.firstName}
          value={firstName}
          onChange={handleChange('firstName')}
        />
      </Box>
      <Box paddingB={16}>
        <Input
          {...register('lastName')}
          label={t('formFields.lastName')}
          placeholder={t('enterLastName')}
          inputMode="text"
          name="lastName"
          test-id="last-name"
          errorText={errors?.lastName?.message}
          error={!!errors?.lastName}
          value={lastName}
          onChange={handleChange('lastName')}
        />
      </Box>
      <Box paddingB={16}>
        <Input
          {...register('alias')}
          label={t('manageBeneficiaries.beneficiaryDescription')}
          inputMode="text"
          name="alias"
          test-id="alias"
          errorText={errors?.alias?.message}
          error={!!errors?.alias}
          placeholder={t('inputPlaceholders.enterBeneficiaryDescriptionPlaceholder')}
          value={alias}
          onChange={handleChange('alias')}
        />
      </Box>
      {!!renderCurrencySpecificFields && <Box>{renderCurrencySpecificFields()}</Box>}
      <Box paddingB={16}>
        <SingleDate
          {...register('dateOfBirth')}
          labels={{
            day: t('formFields.day'),
            month: t('formFields.month'),
            year: t('formFields.year'),
            date: `${t('formFields.dateOfBirth')} ${t('formFields.optional')}`,
            errorMessage: getDateErrorText(errors?.dateOfBirth),
          }}
          date={getDateFields(dateOfBirth)}
          errors={getDateErrors(errors?.dateOfBirth)}
          onChange={date => setValue('dateOfBirth', setDateInputValue(date))}
          onValidate={errors => setDateErrors(errors)}
          errorText={errors?.dateOfBirth?.message}
        />
      </Box>
    </Box>
  )
}
